// src/hooks/useReadAloud.js
// Read-aloud on the browser's own speech engine (window.speechSynthesis): free,
// on-device, no API calls. Voices come from the user's OS and browser, so which
// languages can be read varies by device.
//
// Platform quirks handled here:
//  - Chrome's Google voices go silent after ~15s of continuous speech, so text
//    is fed one sentence-sized chunk at a time.
//  - pause()/resume() is unreliable (broken on Android), so pause cancels and
//    remembers the paragraph; resume restarts that paragraph.
//  - iOS only starts speech from inside a tap, so playFrom() calls speak()
//    synchronously from the click handler.
//  - Voice lists load asynchronously, and late on Safari.
//  - Chrome can garbage-collect a speaking utterance and never fire onend, so
//    the live one is held in a ref.
//  - speak() right after cancel() is occasionally dropped; a watchdog retries.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const synth = typeof window !== 'undefined'
  && 'speechSynthesis' in window
  && typeof window.SpeechSynthesisUtterance === 'function'
  ? window.speechSynthesis
  : null;

export const READ_ALOUD_RATES = [0.75, 1, 1.25];
const RATE_KEY = 'hai_read_aloud_rate';

// Regions to prefer when a language has several voices, best first. Spanish
// readers here are mostly Latin American, Portuguese mostly Brazilian.
const PREFERRED_REGIONS = {
  en: ['us'], es: ['us', 'mx', '419'], pt: ['br'], zh: ['cn'], fr: ['fr', 'ca'],
};

// macOS novelty voices: technically en-US, unusable for reading a document.
const NOVELTY = /\b(Albert|Bad News|Bahh|Bells|Boing|Bubbles|Cellos|Good News|Jester|Organ|Superstar|Trinoids|Whisper|Wobble|Zarvox)\b/i;
// Apple's Eloquence voices exist in many languages and sound robotic.
const ELOQUENCE = /\b(Eddy|Flo|Grandma|Grandpa|Reed|Rocko|Sandy|Shelley)\b/;

// "en-US", "en_US", "cmn-Hans-CN" → { primary: 'en', region: 'us' }
function parseLang(tag) {
  const parts = String(tag || '').toLowerCase().replace(/_/g, '-').split('-');
  let primary = parts[0];
  if (primary === 'cmn') primary = 'zh';
  const region = parts.slice(1).find(p => /^[a-z]{2}$|^\d{3}$/.test(p)) || '';
  return { primary, region };
}

function scoreVoice(v, lang) {
  const { primary, region } = parseLang(v.lang);
  if (primary !== lang) return -1;
  if (lang === 'zh' && region === 'hk') return -1; // Cantonese, not Mandarin
  if (NOVELTY.test(v.name)) return -1;
  let s = 10;
  const pref = PREFERRED_REGIONS[lang] || [];
  const ri = pref.indexOf(region);
  if (ri >= 0) s += 20 - ri * 3;
  if (/natural|neural/i.test(v.name)) s += 40;           // Edge's online voices
  else if (/premium|enhanced/i.test(v.name)) s += 30;    // Apple's downloaded voices
  else if (/google/i.test(v.name)) s += 25;              // Chrome's network voices
  if (ELOQUENCE.test(v.name)) s -= 15;
  if (v.default) s += 2;
  return s;
}

export function pickVoice(voices, lang, exclude) {
  let best = null;
  let bestScore = -1;
  for (const v of voices) {
    if (exclude && exclude.has(v.voiceURI)) continue;
    const s = scoreVoice(v, lang);
    if (s > bestScore) { best = v; bestScore = s; }
  }
  return best;
}

// Split one line of text into chunks short enough to dodge Chrome's ~15s
// cutoff: sentences first, then clause or word breaks inside long sentences.
export function splitForSpeech(text, lang) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return [];
  const max = lang === 'zh' ? 90 : 220; // CJK packs more speech per character
  let sentences;
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    sentences = Array.from(new Intl.Segmenter(lang, { granularity: 'sentence' }).segment(t), s => s.segment.trim());
  } else {
    sentences = (t.match(/[^.!?。！？؟]+[.!?。！？؟]*/g) || [t]).map(s => s.trim());
  }
  const out = [];
  for (const s of sentences) {
    let rest = s;
    while (rest.length > max) {
      const win = rest.slice(0, max);
      let cut = Math.max(
        win.lastIndexOf(', '), win.lastIndexOf('; '), win.lastIndexOf(': '),
        win.lastIndexOf('，'), win.lastIndexOf('、'), win.lastIndexOf('،'),
      );
      if (cut < max * 0.4) cut = win.lastIndexOf(' ');
      if (cut < max * 0.4) cut = max - 1;
      out.push(rest.slice(0, cut + 1).trim());
      rest = rest.slice(cut + 1).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}

export function detectPlatform() {
  if (typeof navigator === 'undefined') return { os: 'other', browser: 'other' };
  const ua = navigator.userAgent || '';
  const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1; // iPadOS
  const os = /iPhone|iPad|iPod/.test(ua) || touchMac ? 'ios'
    : /Android/.test(ua) ? 'android'
    : /Windows/.test(ua) ? 'windows'
    : /Macintosh|Mac OS X/.test(ua) ? 'mac'
    : /Linux|CrOS/.test(ua) ? 'linux'
    : 'other';
  const browser = /Edg(e|A|iOS)?\//.test(ua) ? 'edge'
    : /SamsungBrowser/.test(ua) ? 'samsung'
    : /Firefox|FxiOS/.test(ua) ? 'firefox'
    : /OPR\//.test(ua) ? 'opera'
    : /Chrome|CriOS/.test(ua) ? 'chrome'
    : /Safari/.test(ua) ? 'safari'
    : 'other';
  return { os, browser };
}

function loadRate() {
  try {
    const r = parseFloat(localStorage.getItem(RATE_KEY));
    return READ_ALOUD_RATES.includes(r) ? r : 1;
  } catch {
    return 1;
  }
}

// lang:   language the text is in (picks the voice)
// chunks: one array of strings per document block; an empty array is a block
//         with nothing to say (page furniture), which playback skips.
// status: 'idle' (closed) | 'playing' | 'paused' | 'ended' | 'error'
export default function useReadAloud(lang, chunks) {
  const [voices, setVoices] = useState(() => (synth ? synth.getVoices() : []));
  const [voicesSettled, setVoicesSettled] = useState(() => !!synth && synth.getVoices().length > 0);
  const [badVoices, setBadVoices] = useState(() => new Set());
  const [status, setStatus] = useState('idle');
  const [current, setCurrent] = useState(null);
  const [rate, setRateState] = useState(loadRate);

  const voice = useMemo(() => pickVoice(voices, lang, badVoices), [voices, lang, badVoices]);

  const genRef = useRef(0);          // bumps on every cancel; stale events compare against it
  const uttRef = useRef(null);
  const posRef = useRef({ b: 0, c: 0 });
  const watchdogRef = useRef(null);
  const chunksRef = useRef(chunks);
  const voiceRef = useRef(voice);
  const rateRef = useRef(rate);
  const voicesRef = useRef(voices);
  const badRef = useRef(badVoices);
  const langRef = useRef(lang);
  chunksRef.current = chunks;
  voiceRef.current = voice;
  rateRef.current = rate;
  voicesRef.current = voices;
  badRef.current = badVoices;
  langRef.current = lang;

  // Voices arrive late (voiceschanged on Chrome, sometimes never on Safari), so
  // also poll for a few seconds before concluding there are none.
  useEffect(() => {
    if (!synth) return undefined;
    const load = () => {
      const v = synth.getVoices();
      if (v.length) { setVoices(v); setVoicesSettled(true); }
    };
    load();
    let tries = 0;
    const timer = setInterval(() => {
      load();
      if (++tries >= 12) { clearInterval(timer); setVoicesSettled(true); }
    }, 250);
    if (synth.addEventListener) synth.addEventListener('voiceschanged', load);
    else synth.onvoiceschanged = load;
    return () => {
      clearInterval(timer);
      if (synth.removeEventListener) synth.removeEventListener('voiceschanged', load);
      else synth.onvoiceschanged = null;
    };
  }, []);

  const halt = useCallback(() => {
    genRef.current++;
    clearTimeout(watchdogRef.current);
    uttRef.current = null;
    if (synth) synth.cancel();
  }, []);

  const run = useCallback((gen, b, c, retried = false) => {
    if (gen !== genRef.current) return;
    const list = chunksRef.current;
    while (b < list.length && (!list[b] || c >= list[b].length)) { b++; c = 0; }
    if (b >= list.length) {
      uttRef.current = null;
      setCurrent(null);
      setStatus('ended');
      return;
    }
    const v = voiceRef.current;
    if (!v) { setStatus('error'); return; }

    posRef.current = { b, c };
    setCurrent(b);
    const u = new window.SpeechSynthesisUtterance(list[b][c]);
    u.voice = v;
    u.lang = v.lang;
    u.rate = rateRef.current;
    let started = false;
    u.onstart = () => { started = true; clearTimeout(watchdogRef.current); };
    u.onend = () => {
      if (gen !== genRef.current) return;
      clearTimeout(watchdogRef.current);
      run(gen, b, c + 1);
    };
    u.onerror = (e) => {
      if (gen !== genRef.current) return;
      if (e.error === 'interrupted' || e.error === 'canceled') return;
      clearTimeout(watchdogRef.current);
      // The voice itself failed (e.g. an online voice with no network): drop
      // it and carry on with the next best one, if there is one.
      const bad = new Set(badRef.current);
      bad.add(v.voiceURI);
      badRef.current = bad;
      setBadVoices(bad);
      const next = pickVoice(voicesRef.current, langRef.current, bad);
      voiceRef.current = next;
      if (next) run(gen, b, c);
      else setStatus('error');
    };
    uttRef.current = u;
    synth.speak(u);
    clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      if (gen !== genRef.current || started || retried) return;
      synth.cancel();
      run(gen, b, c, true);
    }, 3000);
  }, []);

  const firstPlayable = useCallback((from = 0, step = 1) => {
    const list = chunksRef.current;
    for (let i = from; i >= 0 && i < list.length; i += step) {
      if (list[i] && list[i].length) return i;
    }
    return -1;
  }, []);

  const playFrom = useCallback((b) => {
    if (!synth || !voiceRef.current) return;
    const start = firstPlayable(Math.max(0, b));
    if (start < 0) return;
    halt();
    setStatus('playing');
    run(genRef.current, start, 0);
  }, [halt, run, firstPlayable]);

  const start = useCallback(() => playFrom(0), [playFrom]);

  const pause = useCallback(() => {
    halt();
    setStatus('paused');
  }, [halt]);

  const resume = useCallback(() => {
    playFrom(current == null ? 0 : current);
  }, [playFrom, current]);

  const stop = useCallback(() => {
    halt();
    setStatus('idle');
    setCurrent(null);
  }, [halt]);

  const step = useCallback((dir) => {
    const from = current == null ? (dir > 0 ? 0 : chunksRef.current.length - 1) : current + dir;
    const target = firstPlayable(from, dir);
    if (target >= 0) playFrom(target);
  }, [current, firstPlayable, playFrom]);
  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  const setRate = useCallback((r) => {
    setRateState(r);
    rateRef.current = r;
    try { localStorage.setItem(RATE_KEY, String(r)); } catch { /* private mode */ }
    // Rate is fixed per utterance, so restart the current chunk at the new speed.
    if (uttRef.current) {
      const { b, c } = posRef.current;
      halt();
      run(genRef.current, b, c);
    }
  }, [halt, run]);

  // A different text (new document, language or tab) ends the session.
  useEffect(() => {
    setStatus('idle');
    setCurrent(null);
    return () => halt();
  }, [chunks, halt]);

  useEffect(() => {
    window.addEventListener('pagehide', halt);
    return () => window.removeEventListener('pagehide', halt);
  }, [halt]);

  return {
    supported: !!synth,
    voicesSettled,
    voice,
    status,
    active: status !== 'idle',
    playing: status === 'playing',
    current,
    rate,
    start, playFrom, pause, resume, stop, next, prev, setRate,
  };
}
